import { t } from "@lingui/macro";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import {
  AlertColor,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import Alerts from "@/client/components/alerts";
import { AmountFormat, DateFormat } from "@/client/components/custom/common-format";
import ChipLabel from "@/client/components/custom/custom-chip";
import { useAppContext } from "@/client/hooks/context";
import { useUser } from "@/client/services/user";
import PlacementCofirm from "./placement-confirm";

// ─── Types ────────────────────────────────────────────────────────────────────
type Job = {
  id: string;
  title: string;
  salary: number;
  status: string;
};

type Placement = {
  title: string;
  jobs: Job[];
  start_date: string;
  end_date: string;
  company?: { name: string };
  type: string;
  created_at: string;
};

// ─── Styled Components ────────────────────────────────────────────────────────
const FilterBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.75, 2),
  backgroundColor: "#f7faff",
  borderRadius: "12px",
  border: "1px solid #dde8fb",
  marginBottom: theme.spacing(1.5),
}));

const filterSelectSx = {
  fontSize: "13px",
  bgcolor: "#fff",
  borderRadius: "8px",
  "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde8fb" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2" },
};

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "12px 12px 0 0",
  border: "1px solid #eaf0fb",
  borderBottom: "none",
  boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
  maxHeight: "65vh",
  overflowY: "auto",
}));

const StyledTableHead = styled(TableHead)(() => ({
  position: "sticky",
  top: 0,
  zIndex: 2,
  "& .MuiTableCell-root": {
    backgroundColor: "#f7faff",
    fontWeight: 700,
    fontSize: "13px",
    color: "#374151",
    borderBottom: "2px solid #dde8fb",
    whiteSpace: "nowrap",
    padding: "10px 14px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "background-color 0.15s",
  "&:hover": { backgroundColor: "#f0f6ff" },
  "&:last-child .MuiTableCell-root": { borderBottom: "none" },
  "& .MuiTableCell-root": {
    fontSize: "13px",
    color: "#374151",
    borderBottom: "1px solid #eaf0fb",
    padding: "10px 14px",
  },
}));

// ─── Status Chip ──────────────────────────────────────────────────────────────
const StatusChip = ({ status }: { status: string }) => {
  switch (status) {
    case "invited":
      return <ChipLabel color="warning" label={t`Invited`} sx={{ fontSize: "11px", p: "0px", height: "24px" }} />;
    case "accepted":
      return <ChipLabel color="success" label={t`Accepted`} sx={{ fontSize: "11px", p: "0px", height: "24px" }} />;
    case "declined":
      return <ChipLabel color="error" label={t`Declined`} sx={{ fontSize: "11px", p: "0px", height: "24px" }} />;
    case "offered":
      return <ChipLabel color="info" label={t`Offered`} sx={{ fontSize: "11px", p: "0px", height: "24px" }} />;
    case "placed":
      return <ChipLabel color="success" label={t`Placed`} sx={{ fontSize: "11px", p: "0px", height: "24px" }} />;
    case "rejected":
      return <ChipLabel color="error" label={t`Rejected`} sx={{ fontSize: "11px", p: "0px", height: "24px" }} />;
    case "attended":
      return <ChipLabel color="primary" label={t`Attended`} sx={{ fontSize: "11px", p: "0px", height: "24px" }} />;
    case "ready":
      return <ChipLabel color="warning" label={t`Ready`} sx={{ fontSize: "11px", p: "0px", height: "24px" }} />;
    default:
      return null;
  }
};

// ─── Constants ────────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const DISABLED_STATUSES = ["accepted", "declined", "offered", "placed", "rejected", "attended"];

// ─── Main Component ───────────────────────────────────────────────────────────
const PlacementPage = () => {
  const [data, setData] = useState<Placement[]>([]);
  const { user } = useUser();
  const userId = user?.studentId;
  const { loading, setLoading, reloading } = useAppContext();

  const [open, setOpen] = useState(false);
  const [jobId, setJobId] = useState<string>("");
  const [alert, setAlert] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: "",
    severity: "info",
  });

  // ─── Filters ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  // ─── Pagination ─────────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ─── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = () => {
      setLoading(true);
      const timer = setTimeout(async () => {
        try {
          const res = await axios.get(`/api/placement/${userId}`);
          setData(res.data.result);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      }, 1000);
      return () => clearTimeout(timer);
    };
    fetchData();
  }, [reloading, userId, setLoading]);

  // ─── Reset page on filter change ────────────────────────────────────────────
  useEffect(() => {
    setPage(0);
  }, [search, filterType, filterStatus]);

  const handleCloseAlert = () => setAlert({ open: false, message: "", severity: "info" });

  const handleStatusOpen = (id: string) => {
    setOpen(true);
    setJobId(id);
  };

  // ─── Flatten rows ────────────────────────────────────────────────────────────
  const rows = useMemo(
  () =>
    [...data]
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime() // ← changed
      ).flatMap((item: Placement) =>
        item.jobs.map((job: Job) => ({
          id: job.id,
          title: item.title,
          jobTitle: job.title,
          startDate: item.start_date,
          endDate: item.end_date,
          company: item.company?.name || "--",
          type: item.type,
          typeFull: item.type.charAt(0).toUpperCase() + item.type.slice(1).toLowerCase(),
          status: job.status,
          salary: job.salary,
        }))
      ),
    [data]
  );

  // ─── Filter options ──────────────────────────────────────────────────────────
  const typeOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.typeFull)))],
    [rows]
  );
  const statusOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.status)))],
    [rows]
  );

  // ─── Filtered rows ───────────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const query = search.toLowerCase().trim();
    return rows.filter((row) => {
      if (
        query &&
        !row.title.toLowerCase().includes(query) &&
        !row.jobTitle.toLowerCase().includes(query) &&
        !(row.company || "").toLowerCase().includes(query)
      )
        return false;
      if (filterType !== "All" && row.typeFull !== filterType) return false;
      if (filterStatus !== "All" && row.status !== filterStatus) return false;
      return true;
    });
  }, [rows, search, filterType, filterStatus]);

  // ─── Paginated rows (client-side slice) ──────────────────────────────────────
  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const hasActiveFilters = search !== "" || filterType !== "All" || filterStatus !== "All";

  const clearFilters = () => {
    setSearch("");
    setFilterType("All");
    setFilterStatus("All");
  };

  return (
    <Box sx={{ flexGrow: 1, px: 1 }}>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      <PlacementCofirm
        setOpen={setOpen}
        open={open}
        jobId={jobId}
        setAlert={setAlert}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            fontSize: "15px",
            fontWeight: 700,
            color: "primary.main",
            mb: "-2px",
            userSelect: "none",
          }}
        >
          {t`Placement`}
        </Box>
      </Box>

      {/* Filter Bar */}
      <FilterBar sx={{ flexWrap: "nowrap", alignItems: "center", gap: 1, py: 1.25 }}>
        <FilterListIcon sx={{ fontSize: 15, color: "primary.main", flexShrink: 0 }} />
        <Typography
          sx={{ fontSize: "12px", fontWeight: 600, color: "primary.main", whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {t`Filters`}
        </Typography>

        {/* Search */}
        <TextField
          size="small"
          placeholder={t`Search title, job, company...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { fontSize: "12px", borderRadius: "8px", bgcolor: "#fff" },
          }}
          sx={{
            flex: "1 1 140px",
            minWidth: 120,
            maxWidth: 200,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde8fb" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
            "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "none !important", outline: "none !important" },
              "& input:focus": { outline: "none !important", boxShadow: "none !important" },
          }}
        />

        {/* Type Filter */}
        <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 130 }}>
          <InputLabel sx={{ fontSize: "12px" }}>{t`Type`}</InputLabel>
          <Select
            label={t`Type`}
            value={filterType}
            onChange={(e: SelectChangeEvent) => setFilterType(e.target.value)}
            sx={{ ...filterSelectSx, width: "100%", minWidth: "unset", maxWidth: "unset", fontSize: "12px" }}
          >
            {typeOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: "12px" }}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status Filter */}
        <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 130 }}>
          <InputLabel sx={{ fontSize: "12px" }}>{t`Status`}</InputLabel>
          <Select
            label={t`Status`}
            value={filterStatus}
            onChange={(e: SelectChangeEvent) => setFilterStatus(e.target.value)}
            sx={{ ...filterSelectSx, width: "100%", minWidth: "unset", maxWidth: "unset", fontSize: "12px" }}
            MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: "12px", textTransform: "capitalize" }}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Count + Clear */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto", flexShrink: 0 }}>
          {!loading && (
            <Typography sx={{ fontSize: "12px", color: "text.disabled", whiteSpace: "nowrap" }}>
              {hasActiveFilters
                ? `${filteredRows.length} of ${rows.length} placements`
                : `${rows.length} placements`}
            </Typography>
          )}
          {hasActiveFilters && (
            <Chip
              label="Clear"
              size="small"
              onDelete={clearFilters}
              onClick={clearFilters}
              sx={{
                fontSize: "11px",
                bgcolor: "#fff0f0",
                color: "#c62828",
                borderColor: "#ffcdd2",
                "& .MuiChip-deleteIcon": { color: "#c62828" },
              }}
              variant="outlined"
            />
          )}
        </Box>
      </FilterBar>

      {/* Table */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 2,
          }}
        >
          <CircularProgress size={36} />
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            {t`Loading placements...`}
          </Typography>
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            {hasActiveFilters
              ? t`No placements match your filters. Try adjusting or clearing them.`
              : t`No placements found.`}
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            borderRadius: "12px",
            border: "1px solid #eaf0fb",
            boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <StyledTableContainer>
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 900 }}>
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ width: "140px" }}>{t`Title`}</TableCell>
                  <TableCell sx={{ width: "140px" }}>{t`Job Title`}</TableCell>
                  <TableCell sx={{ width: "120px" }}>{t`Company`}</TableCell>
                  <TableCell align="center" sx={{ width: "110px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}>
                      <CalendarTodayIcon sx={{ fontSize: 13 }} />
                      {t`Start Date`}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: "110px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}>
                      <CalendarTodayIcon sx={{ fontSize: 13 }} />
                      {t`End Date`}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: "100px" }}>{t`Salary`}</TableCell>
                  <TableCell align="center" sx={{ width: "80px" }}>{t`Type`}</TableCell>
                  <TableCell align="center" sx={{ width: "100px" }}>{t`Status`}</TableCell>
                  <TableCell align="center" sx={{ width: "120px" }}>{t`Action`}</TableCell>
                </TableRow>
              </StyledTableHead>

              <TableBody>
                {paginatedRows.map((row) => (
                  <StyledTableRow key={row.id}>

                    {/* Title */}
                    <TableCell sx={{ width: "140px", overflow: "hidden" }}>
                      <Tooltip title={row.title} placement="top">
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "13px",
                            color: "primary.main",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.title}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Job Title */}
                    <TableCell sx={{ width: "140px", overflow: "hidden" }}>
                      <Tooltip title={row.jobTitle} placement="top">
                        <Typography
                          sx={{
                            fontSize: "13px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.jobTitle}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Company */}
                    <TableCell sx={{ width: "120px", overflow: "hidden" }}>
                      <Tooltip title={row.company} placement="top">
                        <Typography
                          sx={{
                            fontSize: "13px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.company}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Start Date */}
                    <TableCell align="center" sx={{ width: "110px", overflow: "hidden" }}>
                      <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
                        {DateFormat(row.startDate)}
                      </Typography>
                    </TableCell>

                    {/* End Date */}
                    <TableCell align="center" sx={{ width: "110px", overflow: "hidden" }}>
                      <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
                        {DateFormat(row.endDate)}
                      </Typography>
                    </TableCell>

                    {/* Salary */}
                    <TableCell align="center" sx={{ width: "100px", overflow: "hidden" }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1976d2" }}>
                        {AmountFormat(row.salary)}
                      </Typography>
                    </TableCell>

                    {/* Type */}
                    <TableCell align="center" sx={{ width: "80px", overflow: "hidden" }}>
                      <Tooltip title={row.typeFull} arrow>
                        <Chip
                          label={row.type.charAt(0).toUpperCase()}
                          size="small"
                          sx={{
                            fontSize: "11px",
                            fontWeight: 700,
                            width: "32px",
                            minWidth: "32px",
                            bgcolor: "#f0f4ff",
                            color: "#1976d2",
                            cursor: "default",
                          }}
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>

                    {/* Status */}
                    <TableCell align="center" sx={{ width: "100px", overflow: "hidden" }}>
                      <StatusChip status={row.status} />
                    </TableCell>

                    {/* Action */}
                    <TableCell align="center" sx={{ width: "120px", overflow: "hidden" }}>
                      <Stack direction="row" justifyContent="center">
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          disabled={DISABLED_STATUSES.includes(row.status)}
                          onClick={() => handleStatusOpen(row.id)}
                          sx={{
                            textTransform: "none",
                            fontSize: "11px",
                            height: "26px",
                            width: "100px",
                            minWidth: "100px",
                          }}
                        >
                          {t`Confirmation`}
                        </Button>
                      </Stack>
                    </TableCell>

                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>

          {/* Pagination — identical to Assessments/Webinars/Interviews */}
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid #eaf0fb",
              bgcolor: "#f7faff",
              fontSize: "12px",
              "& .MuiTablePagination-toolbar": { minHeight: "40px" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "12px",
              },
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default PlacementPage;
